import React from "react";
import {
  AreaChart,
  Area,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from "recharts";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";

const COLORS = ["#2563EB", "#F97316", "#6B21A8", "#10B981", "#EF4444", "#06B6D4", "#EC4899"];

const CURRENCY_COLORS = {
  USD: "#2563EB",
  ETH: "#F97316",
  HUMACOIN: "#6B21A8",
  USDT: "#10B981",
  BTC: "#EAB308",
};

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload || !payload.length) return null;

  return (
    <div className="bg-white border border-slate-200 rounded-lg px-4 py-3 shadow-lg">
      <p className="text-sm font-semibold text-slate-800 mb-1">{label || payload[0]?.name}</p>
      {payload.map((entry, index) => (
        <p key={index} className="text-sm" style={{ color: entry.color || entry.payload?.fill }}>
          {entry.name}: <span className="font-bold">{typeof entry.value === "number" ? `$${entry.value.toLocaleString()}` : entry.value}</span>
        </p>
      ))}
    </div>
  );
};

export const DonationTrendChart = ({ data, title = "Donation Trends" }) => {
  return (
    <Card data-testid="donation-trend-chart">
      <CardHeader className="pb-2">
        <CardTitle className="text-lg font-heading">{title}</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="h-[300px]">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={data} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
              <defs>
                <linearGradient id="colorAmount" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#6B21A8" stopOpacity={0.25} />
                  <stop offset="95%" stopColor="#6B21A8" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
              <XAxis
                dataKey="date"
                stroke="hsl(var(--muted-foreground))"
                fontSize={11}
                tickFormatter={(value) => new Date(value).toLocaleDateString("en-US", { month: "short", day: "numeric" })}
              />
              <YAxis
                stroke="hsl(var(--muted-foreground))"
                fontSize={11}
                tickFormatter={(value) => `$${(value / 1000).toFixed(0)}k`}
              />
              <Tooltip content={<CustomTooltip />} />
              <Area
                type="monotone"
                dataKey="amount"
                stroke="#6B21A8"
                strokeWidth={2}
                fill="url(#colorAmount)"
                name="Amount (USD)"
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </CardContent>
    </Card>
  );
};

export const CurrencyDistributionChart = ({ data, title = "Donations by Currency" }) => {
  return (
    <Card data-testid="currency-distribution-chart">
      <CardHeader className="pb-2">
        <CardTitle className="text-lg font-heading">{title}</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="h-[300px]">
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={data}
                cx="50%"
                cy="50%"
                innerRadius={60}
                outerRadius={100}
                paddingAngle={2}
                dataKey="total"
                nameKey="currency"
                label={({ currency, percent }) => `${currency} ${(percent * 100).toFixed(0)}%`}
                labelLine={false}
              >
                {data.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip content={<CustomTooltip />} />
              <Legend />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </CardContent>
    </Card>
  );
};

export const CountryDistributionChart = ({ data, title = "Donations by Country" }) => {
  return (
    <Card data-testid="country-distribution-chart">
      <CardHeader className="pb-2">
        <CardTitle className="text-lg font-heading">{title}</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="h-[300px]">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={data} layout="vertical" margin={{ left: 80 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
              <XAxis
                type="number"
                stroke="hsl(var(--muted-foreground))"
                fontSize={11}
                tickFormatter={(value) => `$${(value / 1000).toFixed(0)}k`}
              />
              <YAxis
                type="category"
                dataKey="country"
                stroke="hsl(var(--muted-foreground))"
                fontSize={11}
                width={70}
              />
              <Tooltip content={<CustomTooltip />} />
              <Bar dataKey="total" fill="#6B21A8" radius={[0, 4, 4, 0]} name="Total (USD)" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </CardContent>
    </Card>
  );
};

export const TopProjectsChart = ({ data, title = "Top Funded Projects" }) => {
  const chartData = data.map((project) => ({
    name: project.title.length > 20 ? project.title.slice(0, 20) + "..." : project.title,
    current: project.current_amount,
    target: project.target_amount,
    progress: ((project.current_amount / project.target_amount) * 100).toFixed(1),
  }));

  return (
    <Card data-testid="top-projects-chart">
      <CardHeader className="pb-2">
        <CardTitle className="text-lg font-heading">{title}</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="h-[300px]">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={chartData} layout="vertical" margin={{ left: 100 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
              <XAxis
                type="number"
                stroke="hsl(var(--muted-foreground))"
                fontSize={11}
                tickFormatter={(value) => `$${(value / 1000).toFixed(0)}k`}
              />
              <YAxis
                type="category"
                dataKey="name"
                stroke="hsl(var(--muted-foreground))"
                fontSize={10}
                width={90}
              />
              <Tooltip content={<CustomTooltip />} />
              <Legend />
              <Bar dataKey="current" fill="#6B21A8" name="Raised" radius={[0, 2, 2, 0]} />
              <Bar dataKey="target" fill="#E9D5FF" name="Target" radius={[0, 2, 2, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </CardContent>
    </Card>
  );
};

export default { DonationTrendChart, CurrencyDistributionChart, CountryDistributionChart, TopProjectsChart };
